/**
 * UpdateVolumeModal Component
 *
 * Modal for updating an existing volume resource.
 * IMPORTANT: Uses MB for file size and file extensions (with dots).
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useUpdateVolume } from '../hooks/useUpdateVolume';
import type { VolumeSanitized } from '../../../types/volume.types';
import { z } from 'zod';

// Frontend schema with MB input
const updateVolumeFormSchema = z.object({
  volumeName: z
    .string()
    .min(1, 'Volume name must be at least 1 character long')
    .max(100, 'Volume name must not exceed 100 characters')
    .optional(),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  maxFileSizeMB: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const num = parseFloat(val);
        return !isNaN(num) && num > 0 && num <= 5120;
      },
      'Max file size must be between 0 and 5120 MB (5 GB)'
    ),
  allowedFileExtensions: z.string().optional(), // Comma-separated string
});

type UpdateVolumeFormData = z.infer<typeof updateVolumeFormSchema>;

interface UpdateVolumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  volume: VolumeSanitized;
}

/**
 * UpdateVolumeModal component
 */
export const UpdateVolumeModal: React.FC<UpdateVolumeModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  volume,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UpdateVolumeFormData>({
    resolver: zodResolver(updateVolumeFormSchema),
  });

  // Populate form with existing volume data
  useEffect(() => {
    if (volume) {
      setValue('volumeName', volume.volumeName);
      setValue('description', volume.description || '');
      setValue(
        'maxFileSizeMB',
        volume.maxFileSize ? volume.maxFileSize.toFixed(2) : ''
      );
      setValue(
        'allowedFileExtensions',
        volume.allowedFileExtensions ? volume.allowedFileExtensions.join(', ') : ''
      );
    }
  }, [volume, setValue]);

  const { mutate: updateVolume, isPending } = useUpdateVolume(tenantId, volume.volumeId, {
    onSuccess: () => {
      setSuccessMessage('Volume updated successfully!');
      setErrorMessage('');
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: UpdateVolumeFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Parse max file size in MB
    // Empty field = null (clear to unlimited), value = number
    const maxFileSize = data.maxFileSizeMB && data.maxFileSizeMB !== ''
      ? parseFloat(data.maxFileSizeMB)
      : null;

    // Parse allowed file extensions (comma-separated)
    // Empty field = null (clear to all extensions), value = array of extensions
    const allowedFileExtensions = data.allowedFileExtensions && data.allowedFileExtensions.trim() !== ''
      ? data.allowedFileExtensions
          .split(',')
          .map((ext) => ext.trim())
          .filter((ext) => ext !== '')
          .map((ext) => ext.startsWith('.') ? ext : `.${ext}`) // Ensure extensions start with dot
      : null;

    // Create update DTO
    const updateData: any = {};
    if (data.volumeName !== volume.volumeName) {
      updateData.volumeName = data.volumeName;
    }
    if (data.description !== volume.description) {
      updateData.description = data.description || undefined;
    }
    // Send maxFileSize if changed (null = unlimited, number = specific limit)
    if (maxFileSize !== volume.maxFileSize) {
      updateData.maxFileSize = maxFileSize;
    }
    // Send allowedFileExtensions if changed (null = all extensions, array = specific extensions)
    const currentExtensions = volume.allowedFileExtensions || null;
    if (JSON.stringify(allowedFileExtensions) !== JSON.stringify(currentExtensions)) {
      updateData.allowedFileExtensions = allowedFileExtensions;
    }

    // Check if any field changed
    if (Object.keys(updateData).length === 0) {
      setErrorMessage('No changes to save');
      return;
    }

    updateVolume(updateData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Update Volume</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Volume Code (Read-Only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Volume Code
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 font-mono">
                {volume.volumeCode}
              </code>
              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                Immutable
              </span>
            </div>
          </div>

          {/* Volume Name */}
          <FormField label="Volume Name" required error={errors.volumeName?.message}>
            <Input
              {...register('volumeName')}
              placeholder="my-volume"
              error={!!errors.volumeName}
            />
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this volume..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Max File Size in MB */}
          <FormField
            label="Max File Size (MB)"
            error={errors.maxFileSizeMB?.message}
            helperText="Maximum size for individual files (leave empty for unlimited)"
          >
            <Input
              {...register('maxFileSizeMB')}
              type="number"
              step="0.01"
              min="0"
              max="5120"
              placeholder="100"
              error={!!errors.maxFileSizeMB}
            />
          </FormField>

          {/* Allowed File Extensions */}
          <FormField
            label="Allowed File Extensions"
            error={errors.allowedFileExtensions?.message}
            helperText="Comma-separated extensions (e.g., .pdf, .jpg, .png). Leave empty for all extensions."
          >
            <Input
              {...register('allowedFileExtensions')}
              placeholder=".pdf, .jpg, .png, .docx"
              error={!!errors.allowedFileExtensions}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
